// Hook for Gantt configuration management
import { useState, useEffect, useCallback } from 'react';
import { GanttConfig } from '../types/gantt.types';

const DEFAULT_CONFIG: GanttConfig = {
  viewMode: 'month',
  showWeekends: true,
  showDependencies: true,
  showProgress: true,
  showResources: true,
  showCriticalPath: false,
  showMilestones: true,
  showBaseline: false,
  autoSchedule: false,
  workingDays: [1, 2, 3, 4, 5], // Monday to Friday
  workingHours: {
    start: '09:00',
    end: '17:00'
  },
  holidays: [],
  colors: {
    task: '#f97316',
    milestone: '#8b5cf6',
    group: '#3b82f6',
    critical: '#ef4444',
    delayed: '#dc2626',
    completed: '#10b981'
  }
};

export const useGanttConfig = () => {
  const [config, setConfig] = useState<GanttConfig>(() => {
    // Load config from localStorage
    const savedConfig = localStorage.getItem('gantt_config');
    if (savedConfig) {
      try {
        return { ...DEFAULT_CONFIG, ...JSON.parse(savedConfig) };
      } catch (error) {
        console.error('Error parsing saved config:', error);
      }
    }
    return DEFAULT_CONFIG;
  });

  // Save config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('gantt_config', JSON.stringify(config));
  }, [config]);

  const updateConfig = useCallback((updates: Partial<GanttConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);

  const resetConfig = useCallback(() => {
    setConfig(DEFAULT_CONFIG);
    localStorage.removeItem('gantt_config');
  }, []);

  return {
    config,
    updateConfig,
    resetConfig
  };
};